import { useEffect, useMemo, useState } from 'react';
import { crypto as cv, type dto } from '@photo-vault/shared';
import { Card, Icon } from '../../ui/index.js';
import { useAuthSession } from '../../auth/AuthContext.js';
import { useInfra } from '../../infra/context.js';
import { MediaGrid } from '../viewer/MediaGrid.js';
import { decryptRating } from '../rating/ratingCrypto.js';
import { PhotoSortControl } from '../albums/PhotoSortControl.js';
import {
  loadSortPreference,
  saveSortPreference,
  sortPhotoRows,
  type SortState,
} from '../albums/photoSort.js';

interface Props {
  albumId: string;
  rootId: string;
  albumKey: CryptoKey;
}

interface PhotoRow {
  photo: dto.Photo;
  filename: string;
  mimeType?: string;
  // null while the downloads/batch chunk for this row hasn't arrived yet.
  url: string | null;
  // Decrypted rating 1..5, or null if unrated / undecodable. Read-only here.
  rating: number | null;
}

// Read-only photo grid for viewers. Mirrors AlbumPhotosSection's render path
// but skips the upload flow — viewers can't write to someone else's bucket.
export function ViewerPhotosSection({ albumId, rootId, albumKey }: Props) {
  const session = useAuthSession();
  const { metadata } = useInfra();
  const [rows, setRows] = useState<PhotoRow[] | 'loading' | { error: string }>('loading');
  const [sort, setSort] = useState<SortState>(() => loadSortPreference());

  useEffect(() => {
    let cancelled = false;
    setRows('loading');
    (async () => {
      try {
        const { photos } = await metadata.listAlbumPhotos(albumId, session.jwt, rootId);
        if (cancelled) return;
        if (photos.length === 0) {
          setRows([]);
          return;
        }
        const decryptedMeta = await Promise.all(
          photos.map(async (p) => {
            const ratingPromise = p.encryptedRating
              ? decryptRating(p.encryptedRating, albumKey)
              : Promise.resolve<number | null>(null);
            try {
              const json = await cv.decryptText(p.encryptedMeta, albumKey);
              const meta = JSON.parse(json) as { filename?: string; mimeType?: string };
              const rating = await ratingPromise;
              return { photo: p, filename: meta.filename ?? p.id, mimeType: meta.mimeType, rating };
            } catch {
              const rating = await ratingPromise;
              return { photo: p, filename: '(meta non leggibile)', rating };
            }
          }),
        );
        if (cancelled) return;
        // Render placeholders immediately; stream the presigned URLs in.
        const initialRows: PhotoRow[] = decryptedMeta.map((m) => ({
          photo: m.photo,
          filename: m.filename,
          ...('mimeType' in m && m.mimeType ? { mimeType: m.mimeType } : {}),
          url: null,
          rating: m.rating,
        }));
        setRows(initialRows);

        await metadata.downloadsBatchAllStreaming(
          photos.map((p) => ({ albumId: p.albumId, photoId: p.id })),
          session.jwt,
          (chunk) => {
            if (cancelled) return;
            const byId = new Map(chunk.map((it) => [it.photoId, it.url]));
            setRows((current) => {
              if (current === 'loading' || 'error' in current) return current;
              return current.map((r) => {
                if (r.url) return r;
                const u = byId.get(r.photo.id);
                return u ? { ...r, url: u } : r;
              });
            });
          },
        );
      } catch (err) {
        if (!cancelled) setRows({ error: err instanceof Error ? err.message : 'errore' });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [albumId, albumKey, rootId, session.jwt, metadata]);

  const list = rows as PhotoRow[];
  const sortedList = useMemo(
    () => (Array.isArray(rows) ? sortPhotoRows(rows, sort) : null),
    [rows, sort],
  );

  function handleSortChange(next: SortState) {
    setSort(next);
    saveSortPreference(next);
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-3">
        <h3 className="font-display text-lg font-semibold">Media</h3>
        {Array.isArray(rows) && rows.length > 1 ? (
          <PhotoSortControl value={sort} onChange={handleSortChange} />
        ) : null}
      </div>
      {rows === 'loading' ? (
        <p className="text-sm text-on-surface-variant">Carico media…</p>
      ) : 'error' in rows && !Array.isArray(rows) ? (
        <p className="text-sm text-error">Errore: {(rows as { error: string }).error}</p>
      ) : list.length === 0 ? (
        <Card className="flex flex-col items-center gap-2 py-12 text-center">
          <Icon name="photo_library" className="text-4xl text-on-surface-variant" />
          <p className="text-sm text-on-surface-variant">Nessun media in questo album.</p>
        </Card>
      ) : (
        <MediaGrid
          items={(sortedList ?? list).map((r) => ({
            id: r.photo.id,
            url: r.url,
            filename: r.filename,
            ...(r.mimeType ? { mimeType: r.mimeType } : {}),
            rating: r.rating,
            info: {
              encryptedMeta: r.photo.encryptedMeta,
              uploadedAt: r.photo.createdAt,
              encryptedSize: r.photo.size,
              ...(r.photo.keyGen !== undefined ? { keyGen: r.photo.keyGen } : {}),
            },
          }))}
          albumKey={albumKey}
        />
      )}
    </div>
  );
}
